import { Injectable } from '@angular/core';
import { ActionContributionsService } from '@ansys-internal/awf-angular/contributions';
import { PageStateAction } from '../../actions/page-state.action';
import {
  PageStateExecuteEnum,
  PageStateInput,
  PageStateModel,
  PageStateValueMap,
} from '../../state/lib/app-page.state';
import {
  CompareJobsExecuteEnum,
  CompareJobsValueMap,
} from '../../state/lib/compare-jobs.state';
import { CompareJobsAction } from '../../actions/compare-jobs.action';

type ActionMapEntry<T> = {
  action: any;
  enum: any;
  property: string;
} & { _type?: T };

function defineActionMap<
  T extends {
    [K in PageStateExecuteEnum]: ActionMapEntry<any>;
  }
>(map: T): T {
  return map;
}

type EnumActionMap = typeof enumActionMap;

export type ValidKeys = keyof EnumActionMap;

// TODO: Move this to state files and contribute to this map
const enumActionMap = defineActionMap({
  [PageStateExecuteEnum.SET_SECTION]: {
    action: PageStateAction,
    enum: PageStateExecuteEnum,
    property: 'section',
    _type:
      undefined as unknown as PageStateValueMap[PageStateExecuteEnum.SET_SECTION],
  },
  [PageStateExecuteEnum.SET_SUBSECTION]: {
    action: PageStateAction,
    enum: PageStateExecuteEnum,
    property: 'subsection',
    _type:
      undefined as unknown as PageStateValueMap[PageStateExecuteEnum.SET_SUBSECTION],
  },
  [PageStateExecuteEnum.SET_RESULT_ID]: {
    action: PageStateAction,
    enum: PageStateExecuteEnum,
    property: 'resultId',
    _type:
      undefined as unknown as PageStateValueMap[PageStateExecuteEnum.SET_RESULT_ID],
  },
  [PageStateExecuteEnum.SET_COMPARISON_TOOL_JOB_IDS]: {
    action: PageStateAction,
    enum: PageStateExecuteEnum,
    property: 'comparisonToolJobIds',
    _type:
      undefined as unknown as PageStateValueMap[PageStateExecuteEnum.SET_COMPARISON_TOOL_JOB_IDS],
  },
  [CompareJobsExecuteEnum.SET_JOBS]: {
    action: CompareJobsAction,
    enum: CompareJobsExecuteEnum,
    property: 'jobs',
    _type: undefined as unknown as CompareJobsValueMap['SET_JOBS'],
  },
  [CompareJobsExecuteEnum.ADD_RESULT_TO_COMPARE]: {
    action: CompareJobsAction,
    enum: CompareJobsExecuteEnum,
    property: 'resultsToCompare',
    _type: undefined as unknown as CompareJobsValueMap['ADD_RESULT_TO_COMPARE'],
  },
  [CompareJobsExecuteEnum.GET_RESULT_TO_COMPARE]: {
    action: CompareJobsAction,
    enum: CompareJobsExecuteEnum,
    property: 'resultId',
    _type: undefined as unknown as CompareJobsValueMap['GET_RESULT_TO_COMPARE'],
  },
  [CompareJobsExecuteEnum.REMOVE_RESULT_FROM_COMPARE]: {
    action: CompareJobsAction,
    enum: CompareJobsExecuteEnum,
    property: 'resultId',
    _type:
      undefined as unknown as CompareJobsValueMap['REMOVE_RESULT_FROM_COMPARE'],
  },
  [CompareJobsExecuteEnum.CLEAR_RESULTS_FROM_COMPARE]: {
    action: CompareJobsAction,
    enum: CompareJobsExecuteEnum,
    property: 'resultsToCompare',
    _type:
      undefined as unknown as CompareJobsValueMap['CLEAR_RESULTS_FROM_COMPARE'],
  },
});
export type ValueType<K extends ValidKeys> = K extends keyof CompareJobsValueMap
  ? CompareJobsValueMap[K]
  : K extends keyof PageStateValueMap
  ? PageStateValueMap[K]
  : never;

/**
 * ConceptEV's execute
 * Creates an action and input for the given key and value.
 *
 * @param key
 * @param value
 */
@Injectable({ providedIn: 'root' })
export class CevActionsService {
  constructor(private actions: ActionContributionsService) {}

  execute<K extends ValidKeys>(key: K, value: ValueType<K>): any {
    const [action, input] = this.cevExec(key, value);

    return this.actions.execute(action, input);
  }

  private cevExec<K extends ValidKeys>(key: K, value: ValueType<K>): any {
    const payload = {
      type: key,
      [enumActionMap[key].property]: value,
    };
    return [enumActionMap[key].action, payload];
  }
}
